import { gql } from "@apollo/client";

/** Step 4 — landscape change: series + KPIs. */
export const STORY_LANDSCAPE_CHANGE_QUERY = gql`
  query StoryLandscapeChange(
    $territoryIds: [ID!]
    $dateFrom: String
    $dateTo: String
    $metric: String
  ) {
    landCoverSeries(
      territoryIds: $territoryIds
      metric: $metric
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      indicateurId
      libelle
      unit
      points {
        mesureId
        periodStart
        value
        unit
        territoryId
        territoryLabel
        dimensions {
          type
          code
          label
        }
      }
    }
  }
`;

/** Step 1 — headline wood-use share (single %). */
export const STORY_WOOD_USAGE_QUERY = gql`
  query StoryWoodUsage($indicateurId: ID!, $dateFrom: String, $dateTo: String) {
    indicatorDistribution(
      indicateurId: $indicateurId
      territoryIds: null
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      indicateurId
      libelle
      sampleSize
      buckets {
        modalityLabel
        value
        unit
      }
    }
  }
`;

/** Step 5 — community perceptions (6 distributions). */
export const STORY_COMMUNITY_PERCEPTIONS_QUERY = gql`
  query StoryCommunityPerceptions(
    $territoryIds: [ID!]
    $dateFrom: String
    $dateTo: String
    $erosionId: ID!
    $waterId: ID!
    $soilsId: ID!
    $forestId: ID!
    $climateId: ID!
    $satisfactionId: ID!
  ) {
    erosion: indicatorDistribution(
      indicateurId: $erosionId
      territoryIds: $territoryIds
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      indicateurId
      libelle
      sampleSize
      territoryLabel
      buckets {
        modalityLabel
        value
        unit
        mesureId
      }
    }
    water: indicatorDistribution(
      indicateurId: $waterId
      territoryIds: $territoryIds
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      indicateurId
      libelle
      sampleSize
      territoryLabel
      buckets {
        modalityLabel
        value
        unit
        mesureId
      }
    }
    soils: indicatorDistribution(
      indicateurId: $soilsId
      territoryIds: $territoryIds
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      indicateurId
      libelle
      sampleSize
      territoryLabel
      buckets {
        modalityLabel
        value
        unit
        mesureId
      }
    }
    forest: indicatorDistribution(
      indicateurId: $forestId
      territoryIds: $territoryIds
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      indicateurId
      libelle
      sampleSize
      territoryLabel
      buckets {
        modalityLabel
        value
        unit
        mesureId
      }
    }
    climate: indicatorDistribution(
      indicateurId: $climateId
      territoryIds: $territoryIds
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      indicateurId
      libelle
      sampleSize
      territoryLabel
      buckets {
        modalityLabel
        value
        unit
        mesureId
      }
    }
    satisfaction: indicatorDistribution(
      indicateurId: $satisfactionId
      territoryIds: $territoryIds
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      indicateurId
      libelle
      sampleSize
      territoryLabel
      buckets {
        modalityLabel
        value
        unit
        mesureId
      }
    }
  }
`;

export const STORY_WOOD_INDICATOR_ID = "nbs-ind-distribution-bois-foret";

export const STORY_SURVEY_INDICATOR_IDS = {
  erosionId: "nbs-ind-distribution-erosion",
  waterId: "nbs-ind-distribution-eau",
  soilsId: "nbs-ind-distribution-productivite-sols",
  forestId: "nbs-ind-distribution-reduction-foret",
  climateId: "nbs-ind-distribution-climat",
  satisfactionId: "nbs-ind-distribution-satisfaction-detail",
} as const;

export const STORY_YEARS = [2005, 2015, 2025] as const;

const STORY_SERIES_FIELDS = gql`
  fragment StorySeriesFields on IndicatorSeries {
    indicateurId
    libelle
    unit
    points {
      value
      unit
      periodStart
      territoryLabel
      mesureId
    }
  }
`;

/** Step 6 — priority SE catalog + measurable valorisation. */
export const STORY_SERVICES_QUERY = gql`
  query StoryEcosystemServices(
    $territoryIds: [ID!]
    $dateFrom: String
    $dateTo: String
    $ecoFrom: String
    $ecoTo: String
    $vetId: ID!
    $ecoUsageId: ID!
    $ecoOptionId: ID!
    $ecoNonUsageId: ID!
    $ecoPfnlId: ID!
    $ecoPecheId: ID!
    $ecoBoisId: ID!
    $ecoChasseId: ID!
    $ecoAgricultureId: ID!
    $ecoMicroclimatId: ID!
    $ecoGenetiquesId: ID!
    $ecoEauId: ID!
    $ecoRegLocaleId: ID!
    $ecoRegClimatGlobalId: ID!
    $ecoServicesCulturelsId: ID!
    $ecoEcotourismeId: ID!
  ) {
    readingN1 {
      priorities {
        id
        libelle
        categorie
      }
    }
    dashboardKpis(filters: { territoryIds: $territoryIds, dateFrom: $dateFrom, dateTo: $dateTo }) {
      cultivatedVariationPct
      forestClearVariationPct
      erosionPerceptionPct
      latestYear
    }
    vet: indicatorSeries(indicateurId: $vetId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...StorySeriesFields
    }
    ecoUsage: indicatorSeries(indicateurId: $ecoUsageId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...StorySeriesFields
    }
    ecoOption: indicatorSeries(indicateurId: $ecoOptionId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...StorySeriesFields
    }
    ecoNonUsage: indicatorSeries(indicateurId: $ecoNonUsageId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...StorySeriesFields
    }
    ecoPfnl: indicatorSeries(indicateurId: $ecoPfnlId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...StorySeriesFields
    }
    ecoPeche: indicatorSeries(indicateurId: $ecoPecheId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...StorySeriesFields
    }
    ecoBois: indicatorSeries(indicateurId: $ecoBoisId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...StorySeriesFields
    }
    ecoChasse: indicatorSeries(indicateurId: $ecoChasseId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...StorySeriesFields
    }
    ecoAgriculture: indicatorSeries(
      indicateurId: $ecoAgricultureId
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...StorySeriesFields
    }
    ecoMicroclimat: indicatorSeries(
      indicateurId: $ecoMicroclimatId
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...StorySeriesFields
    }
    ecoGenetiques: indicatorSeries(
      indicateurId: $ecoGenetiquesId
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...StorySeriesFields
    }
    ecoEau: indicatorSeries(indicateurId: $ecoEauId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...StorySeriesFields
    }
    ecoRegLocale: indicatorSeries(
      indicateurId: $ecoRegLocaleId
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...StorySeriesFields
    }
    ecoRegClimatGlobal: indicatorSeries(
      indicateurId: $ecoRegClimatGlobalId
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...StorySeriesFields
    }
    ecoServicesCulturels: indicatorSeries(
      indicateurId: $ecoServicesCulturelsId
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...StorySeriesFields
    }
    ecoEcotourisme: indicatorSeries(
      indicateurId: $ecoEcotourismeId
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...StorySeriesFields
    }
  }
  ${STORY_SERIES_FIELDS}
`;

export const STORY_SERVICES_QUERY_VARS = {
  vetId: "nbs-ind-eco-valeur-totale-vet",
  ecoUsageId: "nbs-ind-eco-valeur-usage-totale",
  ecoOptionId: "nbs-ind-eco-valeur-option",
  ecoNonUsageId: "nbs-ind-eco-valeur-non-usage",
  ecoPfnlId: "nbs-ind-eco-pfnl-total",
  ecoPecheId: "nbs-ind-eco-peche-continentale",
  ecoBoisId: "nbs-ind-eco-bois-energie-construction",
  ecoChasseId: "nbs-ind-eco-chasse-viande-brousse",
  ecoAgricultureId: "nbs-ind-eco-agriculture-fruits-foret",
  ecoMicroclimatId: "nbs-ind-eco-elevage-microclimat",
  ecoGenetiquesId: "nbs-ind-eco-ressources-genetiques",
  ecoEauId: "nbs-ind-eco-eau-autoconsommation",
  ecoRegLocaleId: "nbs-ind-eco-regulation-locale",
  ecoRegClimatGlobalId: "nbs-ind-eco-regulation-climat-global",
  ecoServicesCulturelsId: "nbs-ind-eco-services-culturels",
  ecoEcotourismeId: "nbs-ind-eco-ecotourisme-esthetique",
} as const;
